import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  List,
  ListItem,
  ListItemText,
  Typography,
  Paper,
  InputBase,
  IconButton,
  Button,
  Tooltip,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import StopRoundedIcon from "@mui/icons-material/StopRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import ReactMarkdown from "react-markdown";
import Plot from "react-plotly.js";
import QueryResultsTable from "./QueryResultsTable";
import StreamingMessage from "./StreamingMessage";
import RetryMessage from "./RetryMessage";

const useStyles = makeStyles((theme) => ({
  chatContainer: {
    display: "flex",
    flexDirection: "column",
    height: "500px",
    width: "100%", // Full width within parent
    borderRadius: 9,
    padding: theme.spacing(1),
    overflow: "hidden",
  },
  chatHistory: {
    flexGrow: 1,
    overflowY: "auto",
    padding: theme.spacing(1),
    height: "400px",
    alignContent: "center",
    scrollbarWidth: "thin",
    scrollbarColor: "rgba(255,255,255,0.08) transparent",
    "&::-webkit-scrollbar": {
      width: "6px",
    },
    "&::-webkit-scrollbar-track": {
      background: "transparent",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "rgba(255,255,255,0.08)",
      borderRadius: "3px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      background: "rgba(255,255,255,0.15)",
    },
  },
  chatInputContainer: {
    padding: theme.spacing(1),
    borderTop: "1px solid #444",
  },
  resultsButton: {
    backgroundColor: "#FFC629",
    color: "#000000",
    borderRadius: "px",
    fontFamily: "Raleway, sans-serif",
    padding: theme.spacing(0.5, 1),
    textTransform: "none",
    "&:hover": {
      backgroundColor: "#FFC629",
      color: "#000000",
    },
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.9rem", // font size for smaller screens
    },
  },
  sourceItem: {
    paddingTop: 2,
    paddingBottom: 2,
    paddingLeft: 8,
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  sourcePart: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '0.85em',
    color: '#E0E0E0',
    fontFamily: 'Poppins, sans-serif',
  },
  separator: {
    color: '#888',
    margin: theme.spacing(0, 0.5),
    fontSize: '1.2em',
  },
}));

const ChatWindow = ({
  chatHistory,
  setChatHistory,
  lastMessageRef,
  chatContainerRef,
  userQuery,
  setUserQuery,
  handleSubmitQuery,
  loadingRes,
  isChatDisabled,
  isSendDisabled,
  isStreaming,
  streamingMessageId,
  stopStreaming,
  onRetry,  // Add this prop
}) => {
  const classes = useStyles();
  const lastBotMessageRef = useRef(null);
  const chatHistoryRef = useRef(null);
  const [expandedMessages, setExpandedMessages] = useState([]);
  const [expandedSources, setExpandedSources] = useState([]);
  const userScrolledUp = useRef(false);

  const toggleExpand = (index) => {
    setExpandedMessages((prev) =>
      prev.includes(index)
        ? prev.filter((i) => i !== index)
        : [...prev, index]
    );
  };

  const toggleSourceExpand = (index) => {
    setExpandedSources((prev) =>
      prev.includes(index)
        ? prev.filter((i) => i !== index)
        : [...prev, index]
    );
  };

  const handleScroll = () => {
    const element = chatHistoryRef.current;
    if (element) {
      const isAtBottom =
        element.scrollHeight - element.scrollTop <= element.clientHeight + 1;
      userScrolledUp.current = !isAtBottom;
    }
  };

  // Auto-scroll behavior when chat history changes
  useEffect(() => {
    if (chatHistory.length === 0) return;

    const lastMessage = chatHistory[chatHistory.length - 1];
    const chatHistoryElement = chatHistoryRef.current;
    
    if (!chatHistoryElement) return;

    if (lastMessage.sender === "user") {
      // For user messages, scroll to bottom to show the full query
      userScrolledUp.current = false;
      chatHistoryElement.scrollTop = chatHistoryElement.scrollHeight;
    } else if (
      (lastMessage.sender === "bot" ||
        lastMessage.sender === "visualization") &&
      !isStreaming
    ) {
      // For bot responses, scroll to the beginning of the response
      if (lastBotMessageRef.current) {
        const containerRect = chatHistoryElement.getBoundingClientRect();
        const messageRect = lastBotMessageRef.current.getBoundingClientRect();
        const relativeTop = messageRect.top - containerRect.top;
        
        chatHistoryElement.scrollTop = chatHistoryElement.scrollTop + relativeTop;
      }
    }
  }, [chatHistory, isStreaming]);

  // Auto-scroll during streaming
  useEffect(() => {
    if (isStreaming && chatHistoryRef.current && !userScrolledUp.current) {
      const chatHistoryElement = chatHistoryRef.current;
      chatHistoryElement.scrollTop = chatHistoryElement.scrollHeight;
    }
  }, [isStreaming, chatHistory]);

  const handleToggleResults = (index) => {
    const newHistory = [...chatHistory];
    newHistory[index].showResults = !newHistory[index].showResults;
    setChatHistory(newHistory);
  };

  const handleSendOrStop = () => {
    if (isStreaming) {
      stopStreaming();
    } else {
      handleSubmitQuery();
    }
  };

  // Add a handler for retry within messages
  const handleMessageRetry = (messageId) => {
    if (onRetry) {
      onRetry(messageId);
    }
  };

  // Add a handler for cancelling retry
  const handleCancelRetry = (messageId) => {
    setChatHistory((prevHistory) => {
      return prevHistory.map((msg) => {
        if (msg.id === messageId) {
          return {
            ...msg,
            showRetry: false
          };
        }
        return msg;
      });
    });
  };

  return (
    <Box className={classes.chatContainer} ref={chatContainerRef}>
      <Box
        className={classes.chatHistory}
        ref={chatHistoryRef}
        onScroll={handleScroll}
        data-tour="chat-history-area"
      >
        <List>
          {chatHistory.length === 0 ? (
            <Typography
              variant="h7"
              style={{
                color: "grey",
                textAlign: "center",
                marginTop: "16px",
                fontFamily: "Raleway, sans-serif",
              }}
            >
              Select a collection or database to start chatting!
            </Typography>
          ) : (
            <List style={{ paddingTop: 0, paddingBottom: 0 }}>
              {chatHistory.map((chat, index) => {
                const messageKey = chat.id || `chat-${index}`;
                const isLastBotMessage = 
                  index === chatHistory.length - 1 && 
                  (chat.sender === "bot" || chat.sender === "visualization");
                
                const isBotMessage = chat.sender === "bot";
                const isExpanded = expandedMessages.includes(index);
                const isCurrentlyStreaming = 
                  isBotMessage && 
                  isStreaming && 
                  chat.id === streamingMessageId;

                return (
                  <ListItem
                    key={messageKey}
                    ref={
                      isLastBotMessage ? lastBotMessageRef : 
                      index === chatHistory.length - 1 ? lastMessageRef : null
                    }
                    style={{
                      flexDirection: "column",
                      alignItems:
                        chat.sender === "user" ? "flex-end" : "flex-start",
                    }}
                  >
                    <ListItemText
                      primary={
                        <>
                          {isBotMessage ? (
                            <>
                              <StreamingMessage
                                message={chat.message}
                                isStreaming={isCurrentlyStreaming}
                                isLoading={chat.isLoading}
                                hasError={chat.hasError && !chat.showRetry}
                                toggleExpand={() => toggleExpand(index)}
                                isExpanded={isExpanded}
                                citations={chat.citations}
                                pipelineSteps={chat.pipelineSteps}
                                pipelineComplete={chat.pipelineComplete}
                              />
                              {chat.showRetry && (
                                <RetryMessage
                                  message="Connection lost. Would you like to try again?"
                                  onRetry={() => handleMessageRetry(chat.id)}
                                  onCancel={() => handleCancelRetry(chat.id)}
                                  attempt={chat.retryAttempt || 1}
                                  maxAttempts={3}
                                />
                              )}
                            </>
                          ) : (
                          <ReactMarkdown
                              children={chat.message}
                            components={{
                              h3: ({ node, ...props }) => (
                                <Typography
                                  variant="h6"
                                  style={{
                                    fontWeight: "bold",
                                    color: "#FFC629",
                                    marginTop: 8,
                                    marginBottom: 4,
                                  }}
                                  {...props}
                                />
                              ),
                              strong: ({ node, ...props }) => (
                                <strong style={{ color: "#FFC629" }} {...props} />
                              ),
                              p: ({ node, ...props }) => (
                                <Typography
                                  style={{
                                    fontFamily: "Poppins, sans-serif",
                                    fontSize: "0.9em",
                                    whiteSpace: "pre-wrap",
                                    wordBreak: "break-word",
                                    color: "white",
                                    marginBottom: 8,
                                  }}
                                  {...props}
                                />
                              ),
                              ul: ({ node, ...props }) => (
                                <ul
                                  style={{
                                    paddingLeft: "1.2em",
                                    marginTop: 4,
                                    marginBottom: 8,
                                    color: "white",
                                    fontFamily: "Poppins, sans-serif",
                                    fontSize: "0.9em",
                                  }}
                                  {...props}
                                />
                              ),
                              ol: ({ node, ...props }) => (
                                <ol
                                  style={{
                                    paddingLeft: "1.2em",
                                    marginTop: 4,
                                    marginBottom: 8,
                                    color: "white",
                                    fontFamily: "Poppins, sans-serif",
                                    fontSize: "0.9em",
                                  }}
                                  {...props}
                                />
                              ),
                              li: ({ node, ...props }) => (
                                <li style={{ marginBottom: 4 }} {...props} />
                              ),
                              a: ({ node, href, ...props }) => (
                                <a
                                  href={href}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{
                                    color: "#64b5f6",
                                    textDecoration: "underline",
                                  }}
                                  {...props}
                                />
                              ),
                              code: ({
                                node,
                                inline,
                                className,
                                children,
                                ...props
                              }) =>
                                inline ? (
                                  <code
                                    style={{
                                      backgroundColor: "#333",
                                      color: "#FFC629",
                                      borderRadius: 4,
                                      padding: "2px 4px",
                                      fontSize: "0.85em",
                                    }}
                                    {...props}
                                  >
                                    {children}
                                  </code>
                                ) : (
                                  <pre
                                    style={{
                                      backgroundColor: "#1a1a1a",
                                      color: "#FFC629",
                                      padding: "8px 12px",
                                      borderRadius: 6,
                                      overflowX: "auto",
                                      fontSize: "0.85em",
                                      marginBottom: 8,
                                    }}
                                    {...props}
                                  >
                                    <code>{children}</code>
                                  </pre>
                                ),
                            }}
                          />
                          )}
                        </>
                      }
                      secondary={
                        <span style={{
                          display: "inline-block",
                          fontFamily: "Poppins, sans-serif",
                          fontSize: "0.7em",
                          fontWeight: 600,
                          color: chat.sender === "user" ? "#a0a0a0" : "#000",
                          background: chat.sender === "user"
                            ? "rgba(255,255,255,0.06)"
                            : "rgba(255,198,41,0.7)",
                          padding: "2px 8px",
                          borderRadius: "4px",
                          marginTop: "6px",
                          letterSpacing: "0.03em",
                          textTransform: "uppercase",
                        }}>
                          {chat.sender === "user" ? "You" : "ACE"}
                        </span>
                      }
                      secondaryTypographyProps={{
                        component: "div",
                        style: {},
                      }}
                      style={{
                        backgroundColor:
                          chat.sender === "user" ? "#1a1a1a" : "transparent",
                        color: "white",
                        borderRadius: 8,
                        padding: "8px 16px",
                        textAlign: chat.sender === "user" ? "right" : "left",
                        maxWidth: chat.sender === "user" ? "80%" : "85%",
                        marginLeft: chat.sender === "user" ? "auto" : "0",
                        marginRight: chat.sender === "user" ? "0" : "auto",
                      }}
                    />
                    {Array.isArray(chat.sources) && chat.sources.length > 0 && (
                      <Box style={{ marginTop: "12px", width: "100%" }}>
                        <Typography
                          style={{
                            color: "#FFC629",
                            fontFamily: "Poppins, sans-serif",
                            fontWeight: "bold",
                            fontSize: "0.9em",
                            marginBottom: "4px",
                          }}
                        >
                          Sources:
                        </Typography>
                        <List dense style={{ padding: 0 }}>
                          {(expandedSources.includes(index)
                            ? chat.sources
                            : chat.sources.slice(0, 3)
                          ).map((source, i) => (
                            <ListItem
                              key={i}
                              className={classes.sourceItem}
                              disableGutters
                            >
                              {source.split("|").map((part, partIndex, arr) => {
                                const trimmedPart = part.trim();
                                const emojiMatch =
                                  trimmedPart.match(/^(\p{Emoji})/u);
                                const emoji = emojiMatch ? emojiMatch[1] : null;
                                const text = emoji
                                  ? trimmedPart.substring(emoji.length).trim()
                                  : trimmedPart;

                                return (
                                  <React.Fragment key={partIndex}>
                                    <Typography
                                      component="span"
                                      className={classes.sourcePart}
                                    >
                                      {emoji && (
                                        <span
                                          role="img"
                                          aria-label="icon"
                                          style={{
                                            marginRight: "5px",
                                            verticalAlign: "middle",
                                          }}
                                        >
                                          {emoji}
                                        </span>
                                      )}
                                      {text}
                                    </Typography>
                                    {partIndex < arr.length - 1 && (
                                      <span className={classes.separator}>›</span>
                                    )}
                                  </React.Fragment>
                                );
                              })}
                            </ListItem>
                          ))}
                        </List>
                        {chat.sources.length > 3 && (
                          <Tooltip
                            title={expandedSources.includes(index)
                              ? "Show less"
                              : `Show ${chat.sources.length - 3} more`}
                            placement="bottom"
                          >
                            <IconButton
                              onClick={() => toggleSourceExpand(index)}
                              style={{
                                color: "rgba(255,255,255,0.4)",
                                padding: 2,
                                marginTop: 4,
                              }}
                              size="small"
                            >
                              {expandedSources.includes(index) ? (
                                <ExpandLessRoundedIcon fontSize="small" />
                              ) : (
                                <ExpandMoreRoundedIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    )}
                    {chat.visualizationCode && (
                      <Box
                        style={{
                          marginTop: 12,
                          backgroundColor: "#f5f5f5",
                          padding: 10,
                          borderRadius: 10,
                          width: "100%",
                        }}
                      >
                        <Plot
                          data={JSON.parse(chat.visualizationCode).data}
                          layout={JSON.parse(chat.visualizationCode).layout}
                          style={{ width: "100%", minHeight: "400px" }}
                          config={{ responsive: true }}
                        />
                      </Box>
                    )}

                    {Array.isArray(chat.queryResults) &&
                      chat.queryResults.length > 0 && (
                        <>
                          <Button
                            size="small"
                            className={classes.resultsButton}
                            style={{ marginTop: 8 }}
                            onClick={() => handleToggleResults(index)}
                          >
                            {chat.showResults ? "Hide Results" : "Show Results"}
                          </Button>

                          {chat.showResults && (
                            <QueryResultsTable results={chat.queryResults} />
                          )}
                        </>
                      )}
                  </ListItem>
                );
              })}
            </List>
          )}
        </List>
      </Box>

      {!isChatDisabled && (
        <Box className={classes.chatInputContainer}>
          <Paper
            data-tour="chat-input-area"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderRadius: 8,
            }}
          >
            <InputBase
              id="outlined-multiline-flexible"
              placeholder="Message ACE . . . "
              multiline
              maxRows={4}
              value={userQuery}
              onChange={(e) => setUserQuery(e.target.value)}
              onKeyUp={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSendOrStop();
                }
              }}
              style={{
                color: "black",
                fontFamily: "Poppins, sans-serif",
                fontSize: "0.9rem",
                padding: 8,
                width: "-webkit-fill-available",
              }}
              disabled={isStreaming}
            />
            <Tooltip
              title={isSendDisabled ? "Please select a collection or database first" : ""}
            >
              <span>
                <IconButton
                  disabled={(loadingRes && !isStreaming) || isSendDisabled}
                  type="button"
                  className={classes.button}
                  style={{
                    backgroundColor: "#FFC629",
                    borderRadius: 8,
                    margin: "0.2rem",
                    opacity:
                      (loadingRes && !isStreaming) || isSendDisabled ? 0.5 : 1,
                    cursor:
                      (loadingRes && !isStreaming) || isSendDisabled
                        ? "not-allowed"
                        : "pointer",
                  }}
                  onClick={handleSendOrStop}
                >
                  {isStreaming || loadingRes ? (
                    <StopRoundedIcon />
                  ) : (
                    <SendRoundedIcon />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default ChatWindow;
